<script setup lang="ts">
import { Ellipsis, Loader2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { PageHeaderActionItem } from '~/composables/usePageHeaderActions';

const props = withDefaults(defineProps<{
  items: ReadonlyArray<PageHeaderActionItem>;
  triggerLabel?: string;
  emptyLabel?: string;
  sectionOrder?: ReadonlyArray<string>;
  loading?: boolean;
}>(), {
  triggerLabel: 'More',
  emptyLabel: 'No additional actions',
  sectionOrder: () => ['Actions', 'Guides'],
  loading: false,
});

const pendingId = ref('');
const sections = computed(() => {
  const grouped = new Map<string, PageHeaderActionItem[]>();

  for (const item of props.items) {
    const label = item.section?.trim() || 'Actions';
    const items = grouped.get(label) || [];
    items.push(item);
    grouped.set(label, items);
  }

  return Array.from(grouped.entries())
    .map(([label, items]) => ({ label, items }))
    .sort((a, b) => {
      const aIndex = props.sectionOrder.indexOf(a.label);
      const bIndex = props.sectionOrder.indexOf(b.label);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
});

async function handleSelect(item: PageHeaderActionItem) {
  if (props.loading || item.disabled || pendingId.value) return;
  pendingId.value = item.id;
  try {
    await item.action();
  } finally {
    pendingId.value = '';
  }
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <slot name="trigger" :loading="loading || Boolean(pendingId)">
        <Button variant="outline" :disabled="loading || Boolean(pendingId)">
          <Loader2 v-if="loading || pendingId" class="mr-2 size-4 animate-spin" />
          <Ellipsis v-else class="mr-2 size-4" />
          {{ triggerLabel }}
        </Button>
      </slot>
    </DropdownMenuTrigger>

    <DropdownMenuContent align="end" class="w-60">
      <template v-if="sections.length">
        <template v-for="(section, sectionIndex) in sections" :key="section.label">
          <DropdownMenuLabel>{{ section.label }}</DropdownMenuLabel>
          <DropdownMenuItem
            v-for="item in section.items"
            :key="item.id"
            class="gap-2"
            :disabled="loading || Boolean(pendingId) || item.disabled"
            @select="handleSelect(item)"
          >
            <slot name="item" :item="item">
              <component :is="item.icon" v-if="item.icon" class="size-4" />
              <span>{{ item.label }}</span>
            </slot>
          </DropdownMenuItem>
          <DropdownMenuSeparator v-if="sectionIndex < sections.length - 1" />
        </template>
      </template>

      <DropdownMenuItem v-else disabled>
        {{ emptyLabel }}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
